import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Autocomplete from "@mui/material/Autocomplete";
import Avatar from "@mui/material/Avatar";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useAuth } from "@bw/shared/hooks";
import { searchStaffInvitationCandidates, type StaffInvitationCandidate } from "@/libs/staffApi";

export function StaffUserPicker({ orgId, value, onChange, disabled }: {
  orgId: string;
  value: StaffInvitationCandidate | null;
  onChange: (candidate: StaffInvitationCandidate | null) => void;
  disabled: boolean;
}) {
  const { user } = useAuth();
  const [input, setInput] = useState(value?.display_name ?? "");
  const [query, setQuery] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setQuery(input.trim()), 300);
    return () => clearTimeout(timer);
  }, [input]);
  const ready = query.length >= 2 && query === input.trim();
  const search = useQuery({
    queryKey: ["staff-invitation-candidates", orgId, user?.uid, query],
    queryFn: ({ signal }) => searchStaffInvitationCandidates(orgId, query, signal),
    enabled: Boolean(user) && !disabled && !value && ready,
    retry: false,
    staleTime: 0,
    gcTime: 0,
  });
  return <Autocomplete
    value={value}
    inputValue={input}
    disabled={disabled}
    options={!disabled && !value && ready && !search.isError && !search.isFetching ? search.data ?? [] : []}
    filterOptions={options => options}
    getOptionLabel={option => option.display_name}
    isOptionEqualToValue={(option, selected) => option.user_id === selected.user_id}
    getOptionDisabled={option => option.status !== "available"}
    onInputChange={(_, text, reason) => {
      // Async option refresh must not erase what the person is typing.
      if (reason === "reset") return;
      setInput(text);
      if (reason === "input" && value) onChange(null);
    }}
    onChange={(_, candidate) => { setInput(candidate?.display_name ?? ""); onChange(candidate); }}
    loading={!value && input.trim().length >= 2 && (!ready || search.isFetching)}
    noOptionsText={search.isError ? "Search is unavailable. Try again or use an exact email." : input.trim().length < 2 ? "Type at least two characters" : "No discoverable accounts found"}
    renderOption={(props, option) => {
      const { key: _key, ...itemProps } = props;
      return <li {...itemProps} key={option.user_id}>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", minWidth: 0 }}>
          <Avatar aria-hidden="true" src={option.avatar_url ?? undefined} sx={{ width: 32, height: 32 }}>{option.display_name.slice(0, 1)}</Avatar>
          <Stack sx={{ minWidth: 0 }}><Typography sx={{ overflowWrap: "anywhere" }}>{option.display_name}</Typography>
            {option.status !== "available" ? <Typography variant="caption" color="text.secondary">{option.status === "pending" ? "Invitation pending" : "Already a member"}</Typography> : null}
          </Stack>
        </Stack>
      </li>;
    }}
    renderInput={params => <TextField {...params} label="Find a BW account" helperText="Search discoverable profiles by name. Use an exact email if you cannot identify the right person." />}
  />;
}
